// Landing-page half of site search: reads the `?highlight=` param search
// results links attach (see withHighlight() in search-results.ts), then finds
// and highlights the first on-page occurrence of the search query, scrolling
// it into view. On video pages, a match found inside the (initially hidden)
// transcript panel activates the transcript tab first.

import { reducedMotion } from './utils';
import { getVideoPlayer, seekTranscriptCue } from './video';

// Matches the states_search index's tokenizer/backend minimum word length.
const MIN_LENGTH = 3;
const HIGHLIGHT_CLASS = 'js-search-highlight';

// Checked in priority order: body/summary prose first (shared by page,
// taxonomy-term and video templates), transcript cues last — a page rarely
// wants both lit up for the same visit.
const CONTAINER_SELECTORS = ['.prose', '[data-transcript] .transcript__text'];

/**
 * Wraps the first case-insensitive occurrence of `query` within `root` in a
 * <mark>, preserving the source text's own casing. Returns the mark, or null
 * if `query` doesn't appear in any of root's text nodes.
 */
function findAndWrap(root: Element, query: string): HTMLElement | null {
  const needle = query.toLowerCase();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = (node as Text).parentElement;
      if (!parent || parent.closest(`script, style, .${HIGHLIGHT_CLASS}`)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const text = node.textContent ?? '';
    const idx = text.toLowerCase().indexOf(needle);
    if (idx === -1) continue;

    const range = document.createRange();
    range.setStart(node, idx);
    range.setEnd(node, idx + needle.length);

    const mark = document.createElement('mark');
    mark.className = HIGHLIGHT_CLASS;
    range.surroundContents(mark);
    return mark;
  }

  return null;
}

// Search API stems and matches words independently, so a multi-word query can
// hit without ever appearing on the page as a contiguous phrase. When the
// full phrase doesn't match, fall back word by word — skipping words below
// MIN_LENGTH — trying from the END of the phrase first. Query phrases here
// tend to read "[connector words] [specific term]" ("... acknowledges that
// slavery"), so the trailing words are more often the actual topic than the
// leading ones.
function candidateQueries(query: string): string[] {
  const words = query.split(/\s+/).filter((w) => w.length >= MIN_LENGTH);
  const candidates = [query, ...words.slice().reverse()];
  return candidates.filter((q, i) => candidates.indexOf(q) === i);
}

function findMatch(query: string): HTMLElement | null {
  for (const candidate of candidateQueries(query)) {
    for (const selector of CONTAINER_SELECTORS) {
      for (const container of Array.from(document.querySelectorAll<HTMLElement>(selector))) {
        const mark = findAndWrap(container, candidate);
        if (mark) return mark;
      }
    }
  }
  return null;
}

export function initSearchHighlight(): void {
  const url = new URL(window.location.href);
  const query = url.searchParams.get('highlight')?.trim() ?? '';
  // Left in the URL (not stripped) so the page stays shareable/reloadable
  // with the same highlight intact.
  if (!query || query.length < MIN_LENGTH) return;

  const mark = findMatch(query);
  if (!mark) return;

  const cue = mark.closest<HTMLElement>('.transcript__seek');
  if (cue) {
    const tab = document.getElementById('tab-transcript');
    if (tab instanceof HTMLElement && tab.getAttribute('aria-selected') !== 'true') {
      tab.click();
    }

    // Jumps the player to this cue's timestamp and marks it current, same as
    // clicking it — minus the auto-play. Independent of the scroll/focus
    // below: the player may still be loading when this resolves.
    void getVideoPlayer().then((player) => {
      if (player) seekTranscriptCue(player, cue);
    });
  }

  // A double rAF lets the transcript tab's display:none -> block swap (above)
  // land before scrollIntoView measures layout.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      mark.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      // Moves a11y focus to the match so screen reader users landing from
      // search know where they ended up, without stealing the scroll position
      // scrollIntoView already set.
      mark.tabIndex = -1;
      mark.focus({ preventScroll: true });
    });
  });
}
